import {Box} from '@chakra-ui/react';
import {motion} from 'framer-motion';

// ═══════════════════════════════════════════════════════════════
// DictationEditFlash
// ═══════════════════════════════════════════════════════════════
//
// One-shot "this just got edited by Smart Dictation" overlay. Sweeps a
// luminous Opal-Blue band top→bottom across a section while a soft tint
// pulses underneath, then quietly fades. Purely visual: pointer-events
// are disabled so the editor underneath stays fully interactive.
//
// The parent should mount this component when a dictation edit lands and
// unmount it after ~3.5s. Re-mount with a fresh `key` to replay the
// animation when the same section is dictated into again.
//
// Visual recipe:
//   • Layer A — soft brand tint that fades in (0 → 1) in ~220ms, holds,
//     then ramps back to ~0 over ~1500ms.
//   • Layer B — a vertically-feathered shine band, ~55% tall, that
//     translates from y=-110% → y=170% in 1.25s with an ease-out cubic.
//     A faint blur softens the leading/trailing edges.
//   • Layer C — a thin glowing scanline that rides on the leading edge
//     of the shine to give it a definite "this is the moving part" cue.
//   • Container fades to 0 at ~1.7s so it disappears cleanly even if the
//     parent forgets to unmount immediately.
//
// Keep this component visually subordinate to the FAB's "Applied to <X>"
// pill — both fire at the same moment and we don't want the section to
// out-shout its own toast.

const OPAL_BLUE = '#1E9FD4';
const OPAL_BLUE_DEEP = '#636363';

const SHINE_GRADIENT = `linear-gradient(
  180deg,
  rgba(30, 159, 212, 0) 0%,
  rgba(30, 159, 212, 0.05) 28%,
  rgba(255, 255, 255, 0.55) 46%,
  rgba(30, 159, 212, 0.65) 50%,
  rgba(255, 255, 255, 0.45) 54%,
  rgba(30, 159, 212, 0.05) 72%,
  rgba(30, 159, 212, 0) 100%
)`;

const TINT_GRADIENT = `linear-gradient(
  135deg,
  ${OPAL_BLUE}1f 0%,
  ${OPAL_BLUE_DEEP}14 100%
)`;

const MotionBox = motion(Box);

export default function DictationEditFlash({borderRadius = 'lg', zIndex = 1}) {
  return (
    <MotionBox
      position="absolute"
      top={0}
      left={0}
      right={0}
      bottom={0}
      pointerEvents="none"
      overflow="hidden"
      borderRadius={borderRadius}
      zIndex={zIndex}
      initial={{opacity: 1}}
      animate={{opacity: 0}}
      transition={{duration: 0.4, delay: 1.7, ease: 'easeOut'}}
      aria-hidden="true"
    >
      <MotionBox
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        background={TINT_GRADIENT}
        initial={{opacity: 0}}
        animate={{opacity: [0, 1, 0.85, 0]}}
        transition={{
          duration: 1.7,
          times: [0, 0.13, 0.45, 1],
          ease: 'easeOut',
        }}
      />
      <MotionBox
        position="absolute"
        left={0}
        right={0}
        height="55%"
        top={0}
        background={SHINE_GRADIENT}
        sx={{filter: 'blur(0.5px)'}}
        initial={{y: '-110%'}}
        animate={{y: '170%'}}
        transition={{
          duration: 1.25,
          delay: 0.05,
          ease: [0.22, 1, 0.36, 1],
        }}
      />
      <MotionBox
        position="absolute"
        left={0}
        right={0}
        height="2px"
        top={0}
        background={`linear-gradient(90deg, transparent, ${OPAL_BLUE}, transparent)`}
        boxShadow={`0 0 12px ${OPAL_BLUE}, 0 0 4px ${OPAL_BLUE}`}
        initial={{y: 0, opacity: 0}}
        animate={{y: ['0%', '100%'], opacity: [0, 1, 1, 0]}}
        transition={{
          duration: 1.25,
          delay: 0.1,
          times: [0, 0.15, 0.85, 1],
          ease: [0.22, 1, 0.36, 1],
        }}
      />
    </MotionBox>
  );
}
